import React, { useEffect, useState } from 'react';
import BrandLogo from '../components/BrandLogo';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Clock, CheckCircle2, AlertCircle, Folder, Send, LogOut, RefreshCw, History } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientJobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});
  const [feedbackStatus, setFeedbackStatus] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('client_token');
    if (!token) {
      navigate('/client/login');
      return;
    }
    fetchJobDetail();
  }, [jobId, navigate]);

  const fetchJobDetail = async () => {
    try {
      const token = localStorage.getItem('client_token');
      const { data } = await axios.get(`${API}/client/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJob(data);
      if (data.client_status) {
        setFeedbackStatus(data.client_status);
      }
      if (data.client_feedback) {
        setFeedbackText(data.client_feedback);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/client/login');
      } else {
        toast.error('Failed to load job details');
        navigate('/client/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (file, index, type = 'original') => {
    const key = `${type}-${index}`;
    setDownloading(prev => ({ ...prev, [key]: true }));
    try {
      const token = localStorage.getItem('client_token');
      const { data } = await axios.get(
        `${API}/client/jobs/${jobId}/download-url`,
        {
          params: { s3_key: file.s3_key, file_name: file.file_name },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      window.open(data.download_url, '_blank');
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download file');
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  const submitFeedback = async () => {
    if (!feedbackStatus) {
      toast.error('Please select a status');
      return;
    }

    setSubmittingFeedback(true);
    try {
      const token = localStorage.getItem('client_token');
      await axios.post(
        `${API}/client/jobs/${jobId}/feedback`,
        { status: feedbackStatus, feedback: feedbackText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Feedback submitted successfully');
      fetchJobDetail();
    } catch (error) {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_name');
    localStorage.removeItem('client_email');
    navigate('/client/login');
  };

  const getStatusColor = (status) => {
    const colors = {
      'New': 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'Revision Requested': 'bg-orange-100 text-orange-800',
      'Revision Delivered': 'bg-teal-100 text-teal-800',
      'Rejected': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-zinc-100 text-zinc-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  // Get the latest version of files (revision if available, otherwise retouched)
  const getLatestFiles = () => {
    if (job?.revisions && job.revisions.length > 0) {
      const latestRevision = job.revisions[job.revisions.length - 1];
      return {
        type: 'revision',
        files: latestRevision.files,
        revision: latestRevision
      };
    }
    if (job?.retouched_files && job.retouched_files.length > 0) {
      return {
        type: 'retouched',
        files: job.retouched_files
      };
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-zinc-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-zinc-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return null;
  }

  const latestFiles = getLatestFiles();
  const hasFilesToReview = latestFiles !== null;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <BrandLogo className="h-8" />
              <span className="text-sm text-zinc-500 hidden sm:block">Client Portal</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/client/dashboard')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Job Header */}
        <div className="bg-white rounded-xl border border-zinc-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Job #{job.id.slice(0, 8)}
              </h1>
              <div className="flex items-center gap-3 text-sm text-zinc-600">
                <Clock className="w-4 h-4" />
                Submitted on {formatDate(job.created_at)}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${getStatusColor(job.status)}`}>
                Status: {job.status}
              </span>
            </div>
          </div>

          {job.notes && (
            <div className="mt-4 p-4 bg-zinc-50 rounded-lg">
              <p className="text-sm text-zinc-600"><strong>Notes:</strong> {job.notes}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Original Uploaded Files */}
          <div className="bg-white rounded-xl border border-zinc-200 p-6">
            <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Your Original Submission
            </h2>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {job.files?.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg border border-zinc-100"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Folder className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-zinc-900 truncate" title={file.relative_path || file.file_name}>
                        {file.relative_path || file.file_name}
                      </p>
                      <p className="text-xs text-zinc-500">{formatFileSize(file.file_size)}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => downloadFile(file, index, 'original')}
                    disabled={downloading[`original-${index}`]}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    {downloading[`original-${index}`] ? '...' : 'Download'}
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-sm text-zinc-500 mt-3">
              Total: {job.files?.length || 0} files ({formatFileSize(job.total_file_size || 0)})
            </p>
          </div>

          {/* Latest Files (Retouched or Revised) */}
          <div className={`bg-white rounded-xl border p-6 ${
            latestFiles?.type === 'revision' ? 'border-teal-200' : 'border-green-200'
          }`}>
            <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2">
              {latestFiles?.type === 'revision' ? (
                <>
                  <RefreshCw className="w-5 h-5 text-teal-600" />
                  Latest Revision (#{latestFiles.revision.revision_number})
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Retouched Files
                </>
              )}
            </h2>
            
            {latestFiles ? (
              <>
                {latestFiles.type === 'revision' && latestFiles.revision.notes && (
                  <p className="text-sm text-teal-700 mb-3 bg-teal-50 p-3 rounded">
                    <strong>Revision Notes:</strong> {latestFiles.revision.notes}
                  </p>
                )}
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {latestFiles.files.map((file, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        latestFiles.type === 'revision' 
                          ? 'bg-teal-50 border-teal-100' 
                          : 'bg-green-50 border-green-100'
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Folder className={`w-5 h-5 flex-shrink-0 ${
                          latestFiles.type === 'revision' ? 'text-teal-600' : 'text-green-600'
                        }`} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-zinc-900 truncate" title={file.relative_path || file.file_name}>
                            {file.relative_path || file.file_name}
                          </p>
                          <p className="text-xs text-zinc-500">{formatFileSize(file.file_size)}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadFile(file, index, latestFiles.type)}
                        disabled={downloading[`${latestFiles.type}-${index}`]}
                        className={latestFiles.type === 'revision' 
                          ? 'text-teal-600 hover:text-teal-700' 
                          : 'text-green-600 hover:text-green-700'
                        }
                      >
                        <Download className="w-4 h-4 mr-1" />
                        {downloading[`${latestFiles.type}-${index}`] ? '...' : 'Download'}
                      </Button>
                    </div>
                  ))}
                </div>
                <p className={`text-sm mt-3 ${
                  latestFiles.type === 'revision' ? 'text-teal-600' : 'text-green-600'
                }`}>
                  {latestFiles.files.length} files available for download
                </p>
              </>
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
                <p className="text-zinc-500">No retouched files uploaded yet</p>
                <p className="text-sm text-zinc-400">Files will appear here once our team completes the work</p>
              </div>
            )}
          </div>
        </div>

        {/* Revision History */}
        {job.revisions && job.revisions.length > 1 && (
          <div className="bg-white rounded-xl border border-zinc-200 p-6 mt-6">
            <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2">
              <History className="w-5 h-5 text-zinc-600" />
              Previous Revisions
            </h2>
            <div className="space-y-4">
              {job.revisions.slice(0, -1).reverse().map((revision, revIndex) => (
                <div key={revIndex} className="bg-zinc-50 rounded-lg border border-zinc-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-zinc-700">
                      Revision #{revision.revision_number}
                    </h3>
                    <span className="text-xs text-zinc-500">
                      {formatDate(revision.uploaded_at)}
                    </span>
                  </div>
                  {revision.notes && (
                    <p className="text-sm text-zinc-600 mb-2">Notes: {revision.notes}</p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {revision.files.slice(0, 3).map((file, fileIndex) => (
                      <Button
                        key={fileIndex}
                        variant="outline"
                        size="sm"
                        onClick={() => downloadFile(file, `prev-${revIndex}-${fileIndex}`, 'prev-revision')}
                        disabled={downloading[`prev-revision-prev-${revIndex}-${fileIndex}`]}
                        className="text-xs"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        {file.file_name}
                      </Button>
                    ))}
                    {revision.files.length > 3 && (
                      <span className="text-xs text-zinc-500 self-center">
                        +{revision.files.length - 3} more files
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Initial Retouched Files (if revisions exist, show these separately) */}
        {job.revisions && job.revisions.length > 0 && job.retouched_files && job.retouched_files.length > 0 && (
          <div className="bg-white rounded-xl border border-zinc-200 p-6 mt-6">
            <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Initial Retouched Files
            </h2>
            <div className="flex flex-wrap gap-2">
              {job.retouched_files.slice(0, 5).map((file, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => downloadFile(file, `initial-${index}`, 'initial-retouched')}
                  disabled={downloading[`initial-retouched-initial-${index}`]}
                  className="text-xs"
                >
                  <Download className="w-3 h-3 mr-1" />
                  {file.file_name}
                </Button>
              ))}
              {job.retouched_files.length > 5 && (
                <span className="text-xs text-zinc-500 self-center">
                  +{job.retouched_files.length - 5} more files
                </span>
              )}
            </div>
          </div>
        )}

        {/* Feedback Section */}
        {hasFilesToReview && (
          <div className="bg-white rounded-xl border border-zinc-200 p-6 mt-6">
            <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2">
              <Send className="w-5 h-5" />
              Your Feedback
            </h2>
            
            {job.client_status && (
              <div className="mb-4 p-4 bg-zinc-50 rounded-lg">
                <p className="text-sm text-zinc-600">
                  <strong>Your last submitted status:</strong>{' '}
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    job.client_status === 'Approved' ? 'bg-green-100 text-green-800' :
                    job.client_status === 'Needs Revision' ? 'bg-orange-100 text-orange-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {job.client_status}
                  </span>
                </p>
                {job.client_feedback && (
                  <p className="text-sm text-zinc-600 mt-2">
                    <strong>Your feedback:</strong> {job.client_feedback}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">Status</Label>
                <Select value={feedbackStatus} onValueChange={setFeedbackStatus}>
                  <SelectTrigger data-testid="feedback-status-select" className="h-11">
                    <SelectValue placeholder="Select your feedback status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Approved">✅ Approved - Looks great!</SelectItem>
                    <SelectItem value="Needs Revision">🔄 Needs Revision - Some changes needed</SelectItem>
                    <SelectItem value="Rejected">❌ Rejected - Does not meet expectations</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Feedback / Comments</Label>
                <Textarea
                  data-testid="feedback-text"
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Share your thoughts, suggestions, or specific revision requests..."
                  className="min-h-[120px]"
                />
              </div>

              <Button
                data-testid="submit-feedback-btn"
                onClick={submitFeedback}
                disabled={submittingFeedback || !feedbackStatus}
                className="w-full sm:w-auto"
              >
                {submittingFeedback ? 'Submitting...' : 'Submit Feedback'}
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-8 py-6 text-center text-sm text-zinc-500">
        <p>Need help? Contact us at <a href="mailto:contact@retouchingpix.com" className="text-blue-600 hover:underline">contact@retouchingpix.com</a></p>
      </footer>
    </div>
  );
};

export default ClientJobDetail;
